import React, { useEffect, useRef, useState } from 'react';
import { useAnimateOnView } from '../../hooks/useIntersectionObserver';
import './LocationSection.css';

/**
 * Sección «Contáctanos» — Cómo llegar (id="contact", ancla del header/footer).
 *
 * Mapa: embed de Google SIN API key. `www.google.com/maps?saddr&daddr&output=embed`
 * redirige a `/maps/embed?origin=mfe&pb=…` (frameable). Por eso el mapa traza la
 * ruta pero la página NO recibe minutos/metros: no se muestran tiempos en el
 * panel (nunca valores inventados). Si algún día hay key de Maps JS + Routes,
 * este componente es el único lugar a cambiar.
 *
 * Privacidad: la ubicación se pide solo al pulsar «Usar mi ubicación», vive en
 * estado local y solo viaja a Google dentro de la URL del mapa. No se guarda
 * ni se manda a analítica.
 */

const CLINIC = {
  name: 'Conectemos Chile',
  street: 'Bombero Adolfo Ossa 1010',
  office: 'Oficina 204',
  city: 'Santiago, Chile',
  phoneDisplay: '+56 9 7800 9119',
  phoneE164: '+56978009119',
  whatsapp: '56978009119',
  email: 'conectemoscl@gmail.com',
} as const;

const FULL_ADDRESS = `${CLINIC.street}, ${CLINIC.office}, ${CLINIC.city}`;
// Destino para Google: la dirección (con código postal), no coordenadas. Las coords del
// embed anterior (-33.4413,-70.6535) eran el CENTRO del viewport, no el pin, y caían
// en Morandé (~300 m al oeste). Vista inicial y ruta usan este mismo texto → mismo pin.
// Validar en producción que el pin coincide con la puerta (salida Paseo Ahumada).
const DESTINATION_QUERY = `${CLINIC.street}, 8320327 Santiago, Chile`;
const WHATSAPP_URL = `https://wa.me/${CLINIC.whatsapp}?text=${encodeURIComponent(
  'Hola, me gustaría agendar una consulta en Conectemos Chile'
)}`;

type TravelMode = 'walking' | 'driving';
type GeoStatus = 'idle' | 'locating' | 'located' | 'denied' | 'unavailable';

const EMBED_FLAG: Record<TravelMode, 'w' | 'd'> = { walking: 'w', driving: 'd' };

const placeEmbedUrl = () =>
  `https://www.google.com/maps?q=${encodeURIComponent(DESTINATION_QUERY)}&z=17&hl=es&output=embed`;

const routeEmbedUrl = (origin: string, mode: TravelMode) =>
  `https://www.google.com/maps?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(
    DESTINATION_QUERY
  )}&dirflg=${EMBED_FLAG[mode]}&hl=es&output=embed`;

/** URL oficial de Maps Directions (api=1). Sin origen → Maps usa la ubicación del dispositivo. */
const directionsUrl = (mode: TravelMode | 'transit', origin?: string) => {
  const params = new URLSearchParams({ api: '1', destination: DESTINATION_QUERY, travelmode: mode });
  if (origin) params.set('origin', origin);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

const GEO_MESSAGES: Record<GeoStatus, string> = {
  idle: '',
  locating: 'Buscando tu ubicación…',
  located: 'Ruta trazada desde tu ubicación. Tiempo y distancia estimados por Google en el mapa.',
  denied:
    'No pudimos acceder a tu ubicación. Puedes escribir una dirección de partida o abrir la ruta en Maps.',
  unavailable:
    'Tu navegador no pudo darnos tu ubicación. Puedes escribir una dirección de partida o abrir la ruta en Maps.',
};

const LocationSection: React.FC = () => {
  const [sectionRef, shouldAnimate] = useAnimateOnView();

  const [mode, setMode] = useState<TravelMode>('walking');
  const [origin, setOrigin] = useState<string | null>(null);
  const [originLabel, setOriginLabel] = useState<'gps' | 'typed' | null>(null);
  const [draft, setDraft] = useState('');
  const [geoStatus, setGeoStatus] = useState<GeoStatus>('idle');
  const [mapLoading, setMapLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Invalida respuestas de geolocalización tras «Cancelar» o desmontaje.
  const geoRequestId = useRef(0);

  useEffect(() => () => { geoRequestId.current += 1; }, []);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const mapSrc = origin ? routeEmbedUrl(origin, mode) : placeEmbedUrl();

  // Cada cambio de src recarga el iframe: mostramos el estado "actualizando" hasta onLoad.
  useEffect(() => { setMapLoading(true); }, [mapSrc]);

  const requestLocation = () => {
    if (!('geolocation' in navigator) || !window.isSecureContext) {
      setGeoStatus('unavailable');
      return;
    }
    const id = ++geoRequestId.current;
    setGeoStatus('locating');
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        if (id !== geoRequestId.current) return;
        setOrigin(`${coords.latitude.toFixed(5)},${coords.longitude.toFixed(5)}`);
        setOriginLabel('gps');
        setDraft('');
        setGeoStatus('located');
      },
      (err) => {
        if (id !== geoRequestId.current) return;
        setGeoStatus(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
    );
  };

  const cancelLocation = () => {
    geoRequestId.current += 1;
    setGeoStatus('idle');
  };

  const submitOrigin = (e: React.FormEvent) => {
    e.preventDefault();
    const value = draft.trim();
    if (!value) return;
    geoRequestId.current += 1;
    setOrigin(value);
    setOriginLabel('typed');
    setGeoStatus('idle');
  };

  const clearRoute = () => {
    setOrigin(null);
    setOriginLabel(null);
    setDraft('');
    setGeoStatus('idle');
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(FULL_ADDRESS);
      setCopied(true);
    } catch {
      // Sin permiso de portapapeles: la dirección sigue visible y seleccionable.
    }
  };

  const statusMessage =
    geoStatus !== 'idle'
      ? GEO_MESSAGES[geoStatus]
      : originLabel === 'typed'
        ? 'Ruta trazada desde la dirección que ingresaste. Tiempo y distancia estimados por Google en el mapa.'
        : '';

  const statusTone = geoStatus === 'denied' || geoStatus === 'unavailable' ? 'warn' : 'info';

  return (
    <section
      ref={sectionRef}
      id="contact"
      className={`location-section section-padding${shouldAnimate ? ' loaded' : ''}`}
      aria-labelledby="location-title"
    >
      <div className="container">
        <header className="section-header ls-header">
          <span className="ls-eyebrow">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
              <circle cx="12" cy="9.5" r="2.5" />
            </svg>
            Contáctanos
          </span>
          <h2 id="location-title" className="section-title ls-title">
            <span>
              Estamos más cerca <br className="ls-br" />
              de lo que <em>imaginas</em>.
            </span>
          </h2>
          <p className="section-subtitle ls-subtitle">
            Encuentra nuestra consulta en el centro de Santiago y elige cómo llegar.
          </p>
        </header>

        <div className="ls-card ls-reveal">
          {/* Panel informativo */}
          <div className="ls-panel">
            <div className="ls-place">
              <span className="ls-place__kicker">Ubicación</span>
              <h3 className="ls-place__name">Visítanos en Santiago</h3>
              <address className="ls-place__address">
                {CLINIC.street}, <strong>{CLINIC.office}</strong>
                <br />
                {CLINIC.city}
              </address>
              <button type="button" className="ls-link" onClick={copyAddress}>
                <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} aria-hidden="true"></i>
                {copied ? 'Dirección copiada' : 'Copiar dirección'}
              </button>
            </div>

            <div className="ls-modes" role="group" aria-label="Cómo quieres llegar">
              <button
                type="button"
                className="ls-mode"
                aria-pressed={mode === 'walking'}
                onClick={() => setMode('walking')}
              >
                <i className="fas fa-person-walking" aria-hidden="true"></i>
                <span>A pie</span>
              </button>
              <button
                type="button"
                className="ls-mode"
                aria-pressed={mode === 'driving'}
                onClick={() => setMode('driving')}
              >
                <i className="fas fa-car-side" aria-hidden="true"></i>
                <span>En auto</span>
              </button>
              <a
                className="ls-mode ls-mode--external"
                href={directionsUrl('transit', origin ?? undefined)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Transporte público (abre Google Maps en una pestaña nueva)"
              >
                <i className="fas fa-bus" aria-hidden="true"></i>
                <span>
                  Transporte
                  <br />
                  público <i className="fas fa-arrow-up-right-from-square ls-mode__ext" aria-hidden="true"></i>
                </span>
              </a>
              {geoStatus === 'locating' ? (
                <button type="button" className="ls-mode ls-mode--locating" onClick={cancelLocation}>
                  <span className="ls-spinner" aria-hidden="true"></span>
                  <span>Cancelar</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="ls-mode"
                  aria-pressed={originLabel === 'gps'}
                  onClick={requestLocation}
                >
                  <i className="fas fa-location-crosshairs" aria-hidden="true"></i>
                  <span>
                    Usar mi
                    <br />
                    ubicación
                  </span>
                </button>
              )}
            </div>

            <form className="ls-origin" onSubmit={submitOrigin} role="search" aria-label="Dirección de partida">
              <label htmlFor="ls-origin-input" className="visually-hidden">
                Dirección de partida
              </label>
              <span className="ls-origin__dot" aria-hidden="true"></span>
              <input
                id="ls-origin-input"
                type="text"
                inputMode="search"
                autoComplete="street-address"
                placeholder="Ingresa tu dirección de partida"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <button type="submit" className="ls-origin__submit" aria-label="Trazar ruta desde esta dirección" disabled={!draft.trim()}>
                <i className="fas fa-magnifying-glass" aria-hidden="true"></i>
              </button>
            </form>

            <p className={`ls-status ls-status--${statusTone}`} role="status" aria-live="polite">
              {statusMessage}
              {origin && geoStatus !== 'locating' && (
                <>
                  {' '}
                  <button type="button" className="ls-link ls-link--inline" onClick={clearRoute}>
                    Quitar ruta
                  </button>
                </>
              )}
            </p>

            <p className="ls-privacy">
              Usamos tu ubicación solo para trazar la ruta. No la guardamos.
            </p>

            <div className="ls-actions">
              <a
                className="ls-btn ls-btn--primary"
                href={directionsUrl(mode, origin ?? undefined)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {origin ? 'Iniciar ruta en Google Maps' : 'Cómo llegar con Google Maps'}
                <i className="fas fa-arrow-up-right-from-square" aria-hidden="true"></i>
              </a>
              <a className="ls-btn ls-btn--outline" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                <i className="fab fa-whatsapp" aria-hidden="true"></i>
                Escríbenos por WhatsApp
              </a>
            </div>
          </div>

          {/* Mapa */}
          <div className="ls-map" data-loading={mapLoading ? 'true' : 'false'}>
            {/* Estado F: visible mientras carga o si el iframe queda bloqueado. */}
            <div className="ls-map__fallback">
              <i className="fas fa-map-location-dot" aria-hidden="true"></i>
              <p>
                {CLINIC.street}, {CLINIC.office}
              </p>
              <a href={directionsUrl(mode, origin ?? undefined)} target="_blank" rel="noopener noreferrer">
                Abrir en Google Maps
              </a>
            </div>
            <iframe
              key={mapSrc}
              src={mapSrc}
              title={
                origin
                  ? `Mapa con la ruta ${mode === 'walking' ? 'a pie' : 'en auto'} hasta Conectemos Chile`
                  : 'Mapa con la ubicación de Conectemos Chile, Bombero Adolfo Ossa 1010, Santiago'
              }
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              onLoad={() => setMapLoading(false)}
            />
            {mapLoading && origin && (
              <span className="ls-map__pill" aria-hidden="true">
                Actualizando ruta…
              </span>
            )}
          </div>
        </div>

        {/* Datos prácticos (sin repetir la dirección del panel) */}
        <ul className="ls-facts ls-reveal">
          <li className="ls-fact">
            <span className="ls-fact__icon" aria-hidden="true">
              <i className="fas fa-clock"></i>
            </span>
            <div>
              <h3 className="ls-fact__title">Horario de atención</h3>
              <p>
                Lun a vie 9:00 – 20:00
                <br />
                Sáb 9:00 – 19:00 · Dom 9:00 – 14:00
              </p>
            </div>
          </li>
          <li className="ls-fact">
            <span className="ls-fact__icon" aria-hidden="true">
              <i className="fas fa-building"></i>
            </span>
            <div>
              <h3 className="ls-fact__title">{CLINIC.office}</h3>
              <p>{CLINIC.street}</p>
            </div>
          </li>
          <li className="ls-fact">
            <span className="ls-fact__icon" aria-hidden="true">
              <i className="fas fa-phone"></i>
            </span>
            <div>
              <h3 className="ls-fact__title">Teléfono</h3>
              <p>
                <a href={`tel:${CLINIC.phoneE164}`}>{CLINIC.phoneDisplay}</a>
              </p>
            </div>
          </li>
          <li className="ls-fact">
            <span className="ls-fact__icon" aria-hidden="true">
              <i className="fas fa-envelope"></i>
            </span>
            <div>
              <h3 className="ls-fact__title">Email</h3>
              <p>
                <a href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>
              </p>
            </div>
          </li>
        </ul>

        <div className="social-links ls-social">
          <a href="https://www.facebook.com/" className="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-facebook-f"></i>
          </a>
          <a
            href="https://www.instagram.com/conectemos.chile?igsh=MXNseDRxZGYyZGxwcA=="
            className="social-link"
            aria-label="Instagram"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-instagram"></i>
          </a>
          <a
            href="https://www.tiktok.com/@psicologo.ngel.da?_t=ZM-8zSsKIxiNRG&_r=1"
            className="social-link"
            aria-label="TikTok"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="fab fa-tiktok"></i>
          </a>
          <a href={WHATSAPP_URL} className="social-link" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer">
            <i className="fab fa-whatsapp"></i>
          </a>
        </div>
      </div>
    </section>
  );
};

export default LocationSection;
